import { useQuery } from "@apollo/client/react"
import { CalendarDays, CheckCircle2, ArrowUpRight } from "lucide-react"
import { Link } from "react-router-dom"
import { cn } from "~/common/cn"
import { Temporal } from "~/common/date"
import { experienceProfilePath, postPath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import { PageLayout } from "~/layouts/page-layout"
import { GraphqlError } from "~/ui/graphql-error"
import { OPERATIONS } from "./calendar-queries"

export function CalendarScreen() {
  useDocumentTitle("My publishing calendar")
  const { data, error, loading } = useQuery(OPERATIONS, { pollInterval: 15000 })
  const workspace = data?.operationsWorkspace
  const zone = workspace?.profile?.timeZone ?? "America/New_York"
  const posts = workspace?.posts ?? []
  const scheduled = posts.filter((p) => p.status === "APPROVED" && p.scheduledAt)
  const ready = posts.filter((p) => p.status === "APPROVED" && !p.scheduledAt)
  const posted = posts.filter((p) => p.status === "POSTED")
  const today = Temporal.Now.zonedDateTimeISO(zone).toPlainDate()
  const weekStart = today.subtract({ days: today.dayOfWeek - 1 })
  const dates = Array.from({ length: 14 }, (_, index) => weekStart.add({ days: index }))
  const weeklyTarget = workspace?.profile?.postsPerWeek ?? 3
  return (
    <PageLayout title="My publishing calendar">
      <div className="mx-auto max-w-7xl space-y-7">
        <div className="rounded-2xl border bg-card p-6 sm:p-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">
            A steady rhythm
          </p>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight">
            Your words, at the right time.
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground">
            Your target is {weeklyTarget} LinkedIn feed posts a week. Recording batches happen
            Friday at {workspace?.profile?.fridayTime ?? "09:00"}. All times below use {zone}.
            Scheduling creates a reminder; posting is manual.
          </p>
          <Link
            className="mt-4 inline-block text-sm text-primary underline"
            to={experienceProfilePath({})}
          >
            Adjust my cadence & reminders
          </Link>
        </div>
        {error && <GraphqlError error={error} />}
        {loading && !data && <p>Loading calendar…</p>}
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-7" aria-label="Two-week calendar">
          {dates.map((date) => {
            const dayPosts = scheduled.filter((post) =>
              Temporal.Instant.from(post.scheduledAt!)
                .toZonedDateTimeISO(zone)
                .toPlainDate()
                .equals(date)
            )
            return (
              <section
                key={date.toString()}
                className={cn(
                  "min-h-36 rounded-xl border p-3",
                  date.equals(today) ? "border-primary bg-accent" : "bg-card"
                )}
              >
                <p className="text-xs font-semibold text-muted-foreground">
                  {date.toLocaleString(undefined, {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })}
                </p>
                {date.dayOfWeek === 5 && (
                  <p className="mt-3 text-xs font-medium text-primary">
                    Recording batch · {workspace?.profile?.fridayTime ?? "09:00"}
                  </p>
                )}
                {dayPosts.map((post) => (
                  <Link
                    key={post.id}
                    to={postPath({ postId: post.id })}
                    className="mt-3 block rounded-lg border bg-background p-2 text-xs"
                  >
                    <span className="font-semibold">
                      {Temporal.Instant.from(post.scheduledAt!)
                        .toZonedDateTimeISO(zone)
                        .toPlainTime()
                        .toString({ smallestUnit: "minute" })}
                    </span>
                    <span className="mt-1 block line-clamp-3">{post.title}</span>
                  </Link>
                ))}
              </section>
            )
          })}
        </div>
        {[
          { title: "Scheduled", posts: scheduled, icon: CalendarDays },
          { title: "Ready to schedule", posts: ready, icon: ArrowUpRight },
          { title: "Posted", posts: posted, icon: CheckCircle2 },
        ].map((group) => (
          <section key={group.title} className="space-y-3">
            <h2 className="flex items-center gap-2 font-semibold">
              <group.icon className="size-4 text-primary" />
              {group.title} <span className="text-muted-foreground">{group.posts.length}</span>
            </h2>
            {!group.posts.length && (
              <p className="rounded-xl border border-dashed p-5 text-sm text-muted-foreground">
                {group.title === "Ready to schedule"
                  ? "Approve a saved draft and it will appear here."
                  : "Nothing here yet."}
              </p>
            )}
            {group.posts.map((post) => (
              <Link
                key={post.id}
                to={postPath({ postId: post.id })}
                className="flex items-center justify-between gap-4 rounded-xl border bg-card p-5 hover:border-primary"
              >
                <div>
                  <h3 className="font-medium">{post.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {post.postedAt || post.scheduledAt
                      ? Temporal.Instant.from((post.postedAt || post.scheduledAt)!)
                          .toZonedDateTimeISO(zone)
                          .toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
                      : "Choose a posting time"}
                  </p>
                </div>
                <ArrowUpRight className="size-4 shrink-0 text-primary" />
              </Link>
            ))}
          </section>
        ))}
      </div>
    </PageLayout>
  )
}
